import { UFO_RULES as R, UFO_BONUS, applyUfoBonusEvent, ufoScore, type UfoBonusEvent } from '../../../../shared/ufoRules';
import type { UfoQuestionResult, FinishPlayAttemptInput } from '../../../../shared/results';
import type { UfoConfig } from './ufoLevels';
import { initialLane, laneCenters, nearestLane } from './laneLayout';
import { advanceCruiseField, createCruiseField, cruiseFieldRules, type CruiseObject } from './cruiseField';
export type UfoPhase = 'ready' | 'loading' | 'awaiting-choice' | 'approach' | 'committed' | 'feedback' | 'cruise' | 'arrival' | 'result';
export type FlightState = {
  phase: UfoPhase; clock: number; questionIndex: number; worldDistance: number; y: number; lane: number;
  laneFrom: number; laneStarted: number; gateX: number; gateSpeed: number; deadline: number;
  questionStarted: number; manual: boolean; score: number; results: UfoQuestionResult[]; revision: number;
  /** Öğrenci kartsız yönlendirme yaptı mı (bu soru). */
  steered: boolean;
  /** Seyir/geri bildirim fazında serbest dikey hedef; soru fazlarında null (şerit merkezi kullanılır). */
  targetY: number | null;
  /** Keşif bonusu: bilgi puanından (`score`) ayrı tutulur. */
  bonusPoints: number; bonusEvents: UfoBonusEvent[][];
  cruiseIndex: number; cruiseStarted: number; field: CruiseObject[];
  lastBonus: { kind: UfoBonusEvent; at: number } | null;
};
export function createFlight(config: UfoConfig): FlightState {
  if (!config.questions.length) throw new Error('Soru bulunamadı.');
  config.questions.forEach(q => laneCenters(q.choices.length));
  const lane = initialLane(config.questions[0].choices.length);
  const y = laneCenters(config.questions[0].choices.length)[lane];
  return { phase: 'ready', clock: 0, questionIndex: 0, worldDistance: 0, y, lane, laneFrom: y, laneStarted: 0,
    gateX: R.startGateX, gateSpeed: 0, deadline: Infinity, questionStarted: 0, manual: false, score: 0, results: [], revision: 0,
    steered: false, targetY: null, bonusPoints: 0, bonusEvents: [], cruiseIndex: 0, cruiseStarted: 0, field: [], lastBonus: null };
}
export function startFlight(s: FlightState) { if (s.phase === 'ready') { s.phase = 'loading'; s.revision++; } }
/** Called only once all current educational images are available. */
export function openFlightQuestion(s: FlightState, config: UfoConfig) {
  if (s.phase !== 'loading') return;
  s.phase = config.timingMode === 'none' ? 'awaiting-choice' : 'approach';
  s.questionStarted = s.clock; s.manual = false; s.steered = false; s.targetY = null; s.gateX = R.startGateX;
  s.lane = nearestLane(s.y, config.questions[s.questionIndex].choices.length);
  s.laneFrom = s.y; s.laneStarted = s.clock;
  s.deadline = config.timingMode === 'none' ? Infinity : s.clock + config.questions[s.questionIndex].approachMs;
  s.gateSpeed = Number.isFinite(s.deadline) ? (s.gateX - R.contactX) / (s.deadline - s.clock) : 0;
  s.revision++;
}
export function chooseLane(s: FlightState, config: UfoConfig, lane: number): boolean {
  if (!['awaiting-choice', 'approach'].includes(s.phase) || !Number.isInteger(lane) || !laneCenters(config.questions[s.questionIndex].choices.length)[lane]) return false;
  s.laneFrom = s.y; s.laneStarted = s.clock; s.lane = lane; s.manual = true; s.phase = 'approach';
  // Repeated choices never postpone contact. The 350 ms lock allows a full lane change.
  s.deadline = Math.min(s.deadline, s.clock + R.selectionApproachMs);
  s.gateSpeed = (s.gateX - R.contactX) / (s.deadline - s.clock); s.revision++;
  return true;
}
const STEERABLE_QUESTION_PHASES = ['awaiting-choice', 'approach'];
const FREE_FLIGHT_PHASES = ['feedback', 'cruise', 'arrival'];

/** Kartsız yönlendirme: şerit değişir fakat seçim kesinleşmez, geçitler hızlanmaz ve bekleme modunda yaklaşma başlamaz. */
export function steerLane(s: FlightState, config: UfoConfig, lane: number): boolean {
  if (!STEERABLE_QUESTION_PHASES.includes(s.phase) || !Number.isInteger(lane) || laneCenters(config.questions[s.questionIndex].choices.length)[lane] === undefined) return false;
  if (lane === s.lane) { s.steered = true; return true; }
  s.laneFrom = s.y; s.laneStarted = s.clock; s.lane = lane; s.targetY = null; s.steered = true; s.revision++;
  return true;
}

/** Parmak/fare ile dikey hedef: soru fazlarında en yakın şeride, seyirde serbest yüksekliğe yönelir. */
export function steerTowardY(s: FlightState, config: UfoConfig, y: number): boolean {
  const target = Math.min(0.92, Math.max(0.08, y));
  if (STEERABLE_QUESTION_PHASES.includes(s.phase)) return steerLane(s, config, nearestLane(target, config.questions[s.questionIndex].choices.length));
  if (!FREE_FLIGHT_PHASES.includes(s.phase)) return false;
  s.laneFrom = s.y; s.laneStarted = s.clock; s.targetY = target;
  return true;
}

function beginCruise(s: FlightState) {
  s.cruiseStarted = s.clock; s.field = createCruiseField(s.cruiseIndex); s.bonusEvents.push([]);
}

function endCruise(s: FlightState) {
  s.cruiseIndex += 1; s.field = [];
}

function collectCruise(s: FlightState) {
  const events = advanceCruiseField(s.field, s.clock - s.cruiseStarted, s.y, s.clock);
  if (!events.length) return;
  const cruise = s.bonusEvents[s.bonusEvents.length - 1];
  for (const event of events) {
    const counted = cruise.filter((e) => e === event).length;
    if (counted >= (event === 'star' ? UFO_BONUS.starsPerCruise : UFO_BONUS.asteroidsPerCruise)) continue;
    cruise.push(event); s.bonusPoints = applyUfoBonusEvent(s.bonusPoints, event); s.lastBonus = { kind: event, at: s.clock };
  }
  s.revision++;
}

function contact(s: FlightState, config: UfoConfig) {
  const q = config.questions[s.questionIndex];
  const correctChoiceIndex = q.choices.findIndex(c => c.correct);
  const correct = s.lane === correctChoiceIndex;
  const scoreDelta = ufoScore(correct); s.score += scoreDelta;
  s.results.push({ level: s.questionIndex + 1, questionId: q.id, choiceCount: q.choices.length,
    selectedChoiceIndex: s.lane, correctChoiceIndex, correct, scoreDelta, scoreAfter: s.score,
    durationMs: Math.round(s.clock - s.questionStarted), decision: s.manual || s.steered ? 'manual' : 'automatic',
    questionPrompt: q.prompt || 'Görsel soru', selectedAnswer: q.choices[s.lane].label || 'Görsel seçenek',
    correctAnswer: q.choices[correctChoiceIndex].label || 'Görsel seçenek',
    questionMediaUrl: q.mediaUrl ?? null, selectedMediaUrl: q.choices[s.lane].mediaUrl ?? null, correctMediaUrl: q.choices[correctChoiceIndex].mediaUrl ?? null });
  s.phase = 'feedback'; s.deadline = s.clock + R.feedbackMs; s.revision++;
}
/** Event-boundary integration: one large debug step and many small frames use the same clock. */
export function advanceFlight(s: FlightState, config: UfoConfig, milliseconds: number) {
  if (!Number.isFinite(milliseconds) || milliseconds <= 0 || s.phase === 'ready' || s.phase === 'result') return;
  let remaining = milliseconds;
  while (remaining > 1e-7 && s.phase !== 'result') {
    const boundary = s.phase === 'approach' ? s.deadline - R.commitMs : s.deadline;
    // Seyirde küçük adımlar: hızlı geçen yıldız/göktaşı hiçbir kare hızında atlanmaz.
    const dt = Math.min(remaining, Math.max(0, boundary - s.clock), s.phase === 'cruise' ? cruiseFieldRules.maxStepMs : Infinity);
    s.clock += dt; remaining -= dt;
    const speed = s.phase === 'approach' || s.phase === 'committed' ? Math.max(R.cruiseSpeed, s.gateSpeed * 1000) : s.phase === 'cruise' || s.phase === 'arrival' ? 0.3 : R.cruiseSpeed;
    s.worldDistance += dt / 1000 * speed;
    if (s.phase === 'approach' || s.phase === 'committed') s.gateX -= s.gateSpeed * dt;
    else if (s.phase === 'feedback' || s.phase === 'cruise' || s.phase === 'arrival') s.gateX -= speed * dt / 1000;
    const targetY = s.targetY ?? laneCenters(config.questions[s.questionIndex].choices.length)[s.lane] ?? s.y;
    const t = Math.min(1, (s.clock - s.laneStarted) / R.laneChangeMs);
    s.y = s.laneFrom + (targetY - s.laneFrom) * t * t * (3 - 2 * t);
    if (s.phase === 'cruise') collectCruise(s);
    if (s.clock + 1e-7 < boundary) { if (remaining > 1e-7) continue; break; }
    if (s.phase === 'approach') { s.phase = 'committed'; s.revision++; }
    else if (s.phase === 'committed') contact(s, config);
    else if (s.phase === 'feedback') {
      s.phase = s.questionIndex === config.questions.length - 1 ? 'arrival' : 'cruise'; s.deadline = s.clock + (s.phase === 'arrival' ? R.arrivalMs : R.cruiseMs);
      if (s.phase === 'cruise') beginCruise(s);
      s.revision++;
    }
    else if (s.phase === 'cruise') {
      endCruise(s); s.questionIndex++; s.phase = 'loading'; s.deadline = Infinity; s.revision++;
      // The runtime preloads every educational image before starting the flight.
      openFlightQuestion(s, config);
    } else if (s.phase === 'arrival') { s.phase = 'result'; s.deadline = Infinity; s.revision++; }
    else break;
  }
}
export function flightResult(s: FlightState, config: UfoConfig): FinishPlayAttemptInput {
  if (s.phase !== 'result' || s.results.length !== config.questions.length) throw new Error('Uçuş henüz tamamlanmadı.');
  const correct = s.results.filter(r => r.correct).length;
  const bonusEvents = s.bonusEvents.map((cruise) => [...cruise]);
  return { score: s.score + s.bonusPoints, lives: 0, correct, wrong: s.results.length - correct, attempts: s.results.length, completed: true,
    details: { templateKey: 'ufo-adventure', timingMode: config.timingMode, questions: s.results.map(r => ({ ...r })),
      bonus: { points: s.bonusPoints, stars: bonusEvents.flat().filter((e) => e === 'star').length, asteroidHits: bonusEvents.flat().filter((e) => e === 'asteroid').length, cruises: bonusEvents } } };
}
